import React, { useState, useEffect, useRef } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Switch,
    FormControl,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Box,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Field } from 'formik';

/**
 * LocationPreferences Component
 * 
 * Renders an accordion section for location-specific preferences with inheritance toggles.
 * Each preference can either inherit from the business/tenant default or be overridden
 * for the specific location.
 * 
 * Features:
 * - Inheritance toggle for each preference
 * - Visual indication of inherited vs custom values
 * - Organized in logical groups (Regional, Business, Documents)
 * - Responsive grid layout
 */

// Default business preferences - these would typically come from tenant settings
const DEFAULT_BUSINESS_PREFERENCES = {
    phoneFormat: '(###) ###-####',
    countryCode: '+1',
    timezone: 'America/Toronto',
    currency: 'CAD',
    currencySymbol: '$',
    dateFormat: 'YYYY-MM-DD',
    postalCodeFormat: 'A1A 1A1',
    invoiceFormat: 'INV-{YYYY}-{####}',
    prescriptionFormat: 'RX-{YYYY}-{####}'
};

// Available options for dropdowns
const TIMEZONE_OPTIONS = [
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' }
];

const CURRENCY_OPTIONS = [
    { value: 'CAD', label: 'CAD ($)', symbol: '$' },
    { value: 'USD', label: 'USD ($)', symbol: '$' },
    { value: 'EUR', label: 'EUR (€)', symbol: '€' },
    { value: 'GBP', label: 'GBP (£)', symbol: '£' }
];

const DATE_FORMAT_OPTIONS = [
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-01-31)' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/31/2025)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/01/2025)' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-01-2025)' }
];

const POSTAL_CODE_FORMATS = [
    { value: 'A1A 1A1', label: 'A1A 1A1 (Canadian)' },
    { value: '12345', label: '12345 (US ZIP)' },
    { value: '12345-6789', label: '12345-6789 (US ZIP+4)' }
];

const LocationPreferences = ({ 
    values, 
    setFieldValue, 
    businessPreferences = DEFAULT_BUSINESS_PREFERENCES 
}) => {
    // Track which preferences are using business defaults vs custom values
    const [useBusinessDefault, setUseBusinessDefault] = useState({
        phoneFormat: true,
        countryCode: true,
        timezone: true,
        currency: true,
        dateFormat: true,
        postalCodeFormat: true,
        invoiceFormat: true,
        prescriptionFormat: true
    });

    const isInitialLoad = useRef(true);

    /**
     * Initialize preference states based on existing values
     * If a value matches business default, assume it's inherited
     * Otherwise, assume it's been customized
     * 
     * Only run on mount and when business preferences change, not when form values change
     */
    useEffect(() => {
        if (businessPreferences && isInitialLoad.current) {
            const newState = {};
            Object.keys(businessPreferences).forEach(key => {
                // Check if current value matches business default
                const currentValue = values.preferences?.[key];
                const businessValue = businessPreferences[key];
                
                // If no current value or it matches business default, use business default
                // If there's a custom value that differs from business default, don't use business default
                newState[key] = !currentValue || currentValue === businessValue || currentValue.trim() === '';
            });
            setUseBusinessDefault(newState);
            isInitialLoad.current = false;
        }
    }, [businessPreferences, values.preferences]); // Include both dependencies but control execution with ref

    // Reset initial load flag when business preferences change
    useEffect(() => {
        isInitialLoad.current = true;
    }, [businessPreferences]);

    /**
     * Handle toggle between business default and custom value
     * @param {string} prefKey - The preference key being toggled
     * @param {boolean} useDefault - Whether to use business default
     */
    const handleToggleChange = (prefKey, useDefault) => {
        // Update the toggle state
        setUseBusinessDefault(prev => ({
            ...prev,
            [prefKey]: useDefault
        }));

        // Set field value based on toggle state
        if (useDefault) {
            // Use business default value
            setFieldValue(`preferences.${prefKey}`, businessPreferences[prefKey]);
        } else {
            // Clear to allow custom input, but keep current value if it exists and differs from default
            const currentValue = values.preferences?.[prefKey];
            const businessValue = businessPreferences[prefKey];
            
            // If current value is empty or matches business default, clear it for custom input
            if (!currentValue || currentValue === businessValue || currentValue.trim() === '') {
                setFieldValue(`preferences.${prefKey}`, '');
            }
            // If there's already a custom value, keep it
        }
    };

    /**
     * Render a preference field with inheritance toggle
     * @param {Object} config - Configuration for the preference field
     */
    const renderPreferenceField = (config) => {
        const {
            key,
            label,
            type = 'text',
            options = [],
            helperText,
            gridSize = 6
        } = config;

        const isUsingDefault = useBusinessDefault[key];
        const fieldName = `preferences.${key}`;
        const currentValue = values.preferences?.[key] || businessPreferences[key];

        return (
            <Grid item xs={12} sm={gridSize} key={key}>
                <Box sx={{ mb: 2 }}>
                    {/* Preference Label and Toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
                            {label}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isUsingDefault}
                                    onChange={(e) => handleToggleChange(key, e.target.checked)}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="caption" color="textSecondary">
                                    Use Business Default
                                </Typography>
                            }
                            labelPlacement="start"
                            sx={{ margin: 0 }}
                        />
                    </Box>

                    {/* Input Field */}
                    {type === 'select' ? (
                        <FormControl fullWidth size="small" disabled={isUsingDefault}>
                            <Field name={fieldName}>
                                {({ field, form }) => (
                                    <Select
                                        {...field}
                                        value={currentValue}
                                        onChange={(e) => form.setFieldValue(fieldName, e.target.value)}
                                        displayEmpty
                                        sx={{
                                            backgroundColor: isUsingDefault ? 'grey.100' : 'white',
                                            '& .MuiSelect-select': {
                                                color: isUsingDefault ? 'text.secondary' : 'text.primary'
                                            }
                                        }}
                                    >
                                        {options.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </Field>
                        </FormControl>
                    ) : (
                        <Field
                            as={TextField}
                            name={fieldName}
                            size="small"
                            fullWidth
                            disabled={isUsingDefault}
                            value={currentValue}
                            placeholder={isUsingDefault ? `Business Default: ${businessPreferences[key]}` : ''}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: isUsingDefault ? 'grey.100' : 'white',
                                    color: isUsingDefault ? 'text.secondary' : 'text.primary'
                                }
                            }}
                        />
                    )}

                    {/* Helper Text */}
                    {helperText && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                            {helperText}
                        </Typography>
                    )}

                    {/* Show inherited value when using business default */}
                    {isUsingDefault && (
                        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                            Using: {businessPreferences[key]}
                        </Typography>
                    )}
                </Box>
            </Grid>
        );
    };

    // Configuration for all preference fields
    const preferenceFields = [
        // Regional Settings
        {
            key: 'phoneFormat',
            label: 'Phone Number Format',
            helperText: 'Format for displaying phone numbers (X = digit)',
            gridSize: 6
        },
        {
            key: 'countryCode',
            label: 'Country Code',
            helperText: 'Default country code for phone numbers',
            gridSize: 6
        },
        {
            key: 'timezone',
            label: 'Timezone',
            type: 'select',
            options: TIMEZONE_OPTIONS,
            helperText: 'Local timezone for this location',
            gridSize: 6
        },
        {
            key: 'postalCodeFormat',
            label: 'Postal Code Format',
            type: 'select',
            options: POSTAL_CODE_FORMATS,
            helperText: 'Format for postal/ZIP codes',
            gridSize: 6
        },

        // Business Settings
        {
            key: 'currency',
            label: 'Currency',
            type: 'select',
            options: CURRENCY_OPTIONS,
            helperText: 'Currency used for pricing and invoices',
            gridSize: 6
        },
        {
            key: 'dateFormat',
            label: 'Date Format',
            type: 'select',
            options: DATE_FORMAT_OPTIONS,
            helperText: 'Format for displaying dates',
            gridSize: 6
        },

        // Document Formats
        {
            key: 'invoiceFormat',
            label: 'Invoice Number Format',
            helperText: 'Template for invoice numbers ({YYYY} = year, {####} = number)',
            gridSize: 6
        },
        {
            key: 'prescriptionFormat',
            label: 'Prescription Number Format',
            helperText: 'Template for prescription numbers',
            gridSize: 6
        }
    ];

    return (
        <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="location-preferences-content"
                id="location-preferences-header"
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Location Preferences
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mr: 2 }}>
                        Configure location-specific formatting and display preferences
                    </Typography>
                </Box>
            </AccordionSummary>
            
            <AccordionDetails>
                <Box sx={{ width: '100%' }}>
                    {/* Regional Settings */}
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                        Regional Settings
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {preferenceFields.slice(0, 4).map(renderPreferenceField)}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Business Settings */}
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                        Business Settings
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {preferenceFields.slice(4, 6).map(renderPreferenceField)}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Document Formats */}
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                        Document Formats
                    </Typography>
                    <Grid container spacing={2}>
                        {preferenceFields.slice(6, 8).map(renderPreferenceField)}
                    </Grid>

                    {/* Information Box */}
                    <Box sx={{ 
                        mt: 3, 
                        p: 2, 
                        backgroundColor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}>
                        <Typography variant="caption" color="textSecondary">
                            <strong>💡 Tip:</strong> When "Use Business Default" is enabled, this location will inherit 
                            settings from your main business preferences. Toggle off to customize specific settings 
                            for this location only.
                        </Typography>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default LocationPreferences;
